import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MessageService } from '../../../services/message.service';
import { MessageDto, MessageAnswerDto } from '../../../models/api/messageDto';
import { LinkifyPipe } from '../../../pipes/linkify.pipe';
import { formatRecipients } from '../../../utilities/message-recipients';

@Component({
  selector: 'app-message-view',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    NgxSpinnerModule,
    DatePipe,
    LinkifyPipe,
    RouterModule
  ],
  templateUrl: './message-view.component.html',
  styleUrls: ['./message-view.component.scss']
})
export class MessageViewComponent implements OnInit {
  message: MessageDto | null = null;
  replyForm!: FormGroup;
  isReplying = false;
  visibleAnswers: MessageAnswerDto[] = [];
  hasHiddenAnswers = false;
  isSentMailbox = false;
  private allAnswers: MessageAnswerDto[] = [];
  private answerId: string | null = null;
  private originalOnly = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.replyForm = this.fb.group({
      answerText: ['', [Validators.required, Validators.maxLength(5000)]]
    });

    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.answerId = this.route.snapshot.queryParamMap.get('answerId');
        this.originalOnly = this.route.snapshot.queryParamMap.get('originalOnly') === 'true';
        this.isSentMailbox = this.route.snapshot.queryParamMap.get('mailbox') === 'sent';
        this.isReplying = false;
        this.replyForm.reset();
        this.loadMessage(id);
      }
    });
  }

  private loadMessage(id: string): void {
    this.spinnerService.show();
    const markAsRead = !this.isSentMailbox;
    this.messageService.getMessage(id, markAsRead).subscribe({
      next: (message) => {
        this.message = message;
        this.sortAnswers();
        this.applyAnswerFilter();
        this.spinnerService.hide();
        if (markAsRead) {
          this.messageService.refreshUnreadCount();
        }
      },
      error: (error) => {
        console.error('Error loading message:', error);
        this.spinnerService.hide();
        this.toast.error('Error loading message', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
        this.router.navigate(['/messages']);
      }
    });
  }

  onBack(): void {
    this.router.navigate(['/messages']);
  }

  onToggleReply(): void {
    this.isReplying = !this.isReplying;
    if (!this.isReplying) {
      this.replyForm.reset();
    }
  }

  onShowFullConversation(): void {
    this.visibleAnswers = [...this.allAnswers];
    this.hasHiddenAnswers = false;
  }

  onSendReply(): void {
    if (this.replyForm.invalid || !this.message) {
      this.replyForm.markAllAsTouched();
      return;
    }

    this.spinnerService.show();
    this.messageService.replyToMessage(this.message.id, {
      answerText: this.replyForm.value.answerText
    }).subscribe({
      next: (answer) => {
        this.spinnerService.hide();
        this.allAnswers = [...this.allAnswers, answer];
        this.visibleAnswers = [...this.allAnswers];
        this.hasHiddenAnswers = false;
        this.replyForm.reset();
        this.isReplying = false;
        this.toast.success('Reply sent successfully', {
          iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
        });
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toast.error('Error sending reply', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
        console.error('Error sending reply:', error);
      }
    });
  }

  onDownloadAttachment(): void {
    if (!this.message?.hasAttachment) return;

    this.messageService.getAttachment(this.message.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.message!.fileName || 'attachment';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading attachment:', error);
        this.toast.error('Error downloading attachment', {
          iconTheme: { primary: 'var(--mat-sys-error)', secondary: '#FFF' }
        });
      }
    });
  }

  private sortAnswers(): void {
    if (this.message?.answers) {
      this.message.answers.sort((a, b) =>
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
      );
      this.allAnswers = [...this.message.answers];
    }
  }

  private applyAnswerFilter(): void {
    if (!this.message) return;

    if (this.originalOnly) {
      // Clicked on the original message — show only the original, no replies
      this.visibleAnswers = [];
      this.hasHiddenAnswers = this.allAnswers.length > 0;
    } else if (this.answerId) {
      // Clicked on a specific answer — show replies up to and including that answer
      const index = this.allAnswers.findIndex(a => a.id === this.answerId);
      if (index !== -1) {
        this.visibleAnswers = this.allAnswers.slice(0, index + 1);
        this.hasHiddenAnswers = this.allAnswers.length > index + 1;
      } else {
        this.visibleAnswers = [...this.allAnswers];
        this.hasHiddenAnswers = false;
      }
    } else {
      // No filter — show full conversation
      this.visibleAnswers = [...this.allAnswers];
      this.hasHiddenAnswers = false;
    }
  }

  getRecipientNames(): string {
    if (!this.message) return '';
    return formatRecipients(this.message.recipientUsers, this.message.recipientRoles);
  }

  getAnswerRecipientNames(answer: MessageAnswerDto): string {
    return formatRecipients(answer.recipientUsers);
  }
}
